/**
 * v3.18-1321 — HR Team Management page.
 *
 * A first-class management surface for the new Teams collection.  Every
 * action here is audited server-side and hits the ``/api/teams`` routes.
 *
 * Capabilities per user directive:
 *   • Create Team          (name, team_type, company, team lead, notes)
 *   • Rename Team          (audited)
 *   • Change Team Lead     (audited)
 *   • Archive Team         (audited, reversible)
 *   • Restore Team
 *   • Add Members          (audited)
 *   • Remove Members       (audited)
 *   • Move Members         (audited, requires confirmation + reason)
 *   • View Team History    (immutable audit rows)
 *
 * Operators never see raw UUIDs / DB keys — team lookups and moves are
 * mediated by friendly names throughout.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, Plus, Users, Archive, RotateCcw, Trash2, ArrowRightLeft,
  History, Edit3, UserPlus, Layers, Building2,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const TYPE_TONES = {
  sales:        'bg-blue-100 text-blue-800',
  vo:           'bg-indigo-100 text-indigo-800',
  concierge:    'bg-teal-100 text-teal-800',
  funding:      'bg-amber-100 text-amber-800',
  applications: 'bg-violet-100 text-violet-800',
  management:   'bg-slate-200 text-slate-800',
};

export default function TeamManagementPage() {
  const [loading, setLoading]   = useState(false);
  const [teams, setTeams]       = useState([]);
  const [types, setTypes]       = useState([]);
  const [companies, setCompanies] = useState([]);
  const [statusFilter, setStatus] = useState('active');
  const [typeFilter, setTypeF]  = useState('all');
  const [q, setQ]               = useState('');
  const [createOpen, setCO]     = useState(false);
  const [detailTeam, setDetail] = useState(null);
  const [historyRows, setHistoryRows] = useState(null);
  const [users, setUsers]       = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter && typeFilter !== 'all')     params.team_type = typeFilter;
      if (q) params.q = q;
      const [t, tt, cs, us] = await Promise.all([
        api.get('/teams', { params }),
        api.get('/team-types'),
        api.get('/companies'),
        api.get('/users'),
      ]);
      setTeams(t.data?.items || []);
      setTypes(tt.data?.team_types || []);
      setCompanies(Array.isArray(cs.data) ? cs.data
                   : (cs.data?.items || cs.data?.companies || []));
      setUsers(Array.isArray(us.data) ? us.data
               : (us.data?.items || us.data?.users || []));
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Failed to load teams.'));
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, q]);

  useEffect(() => { reload(); }, [reload]);

  const openHistory = async (t) => {
    setHistoryRows(null);
    try {
      const r = await api.get(`/teams/${t.id}/history`);
      setHistoryRows({ team: t, items: r.data?.items || [] });
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Load history failed.')); }
  };

  const archive = async (t) => {
    const reason = window.prompt(`Archive "${t.name}"? Provide a reason (audited):`);
    if (reason == null) return;
    try {
      await api.post(`/teams/${t.id}/archive`, { reason });
      toast.success(`${t.name} archived.`);
      reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Archive failed.')); }
  };
  const restore = async (t) => {
    try {
      await api.post(`/teams/${t.id}/restore`, {});
      toast.success(`${t.name} restored.`);
      reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Restore failed.')); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const t of teams) {
      (g[t.team_type] ||= []).push(t);
    }
    return g;
  }, [teams]);

  return (
    <div className="p-6 space-y-4" data-testid="team-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading text-slate-900 flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" /> Team Management
          </h1>
          <p className="text-xs text-slate-500">
            First-class Teams — the authoritative source for VO, Coverage, Sales, Funding,
            Applications and Management team assignments. Every change is audited.
          </p>
        </div>
        <Button onClick={() => setCO(true)} className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="team-create-btn">
          <Plus className="h-4 w-4 mr-1" /> New Team
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Team Type</Label>
              <Select value={typeFilter} onValueChange={setTypeF}>
                <SelectTrigger data-testid="team-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatus}>
                <SelectTrigger data-testid="team-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Search by name</Label>
              <Input value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Search team name…"
                     data-testid="team-search-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/> Loading…</div>}

      {!loading && teams.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-slate-500">
          No teams match the current filters.
        </CardContent></Card>
      )}

      {!loading && Object.entries(grouped).map(([tt, list]) => (
        <Card key={tt}>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Badge className={`text-[10px] uppercase tracking-wider ${TYPE_TONES[tt] || 'bg-slate-100 text-slate-700'}`}>
                {list[0]?.team_type_label || tt}
              </Badge>
              <span className="text-slate-500 font-normal">
                ({list.length} team{list.length === 1 ? '' : 's'})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b">
                <tr>
                  <th className="text-left py-2 px-4">Team</th>
                  <th className="text-left py-2 px-4">Company</th>
                  <th className="text-left py-2 px-4">Lead</th>
                  <th className="text-left py-2 px-4">Members</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(t => (
                  <tr key={t.id} className="border-b hover:bg-slate-50"
                      data-testid={`team-row-${t.id}`}>
                    <td className="py-2 px-4 font-medium text-slate-800">{t.name}</td>
                    <td className="py-2 px-4 text-slate-600">
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-slate-400" />{t.company_name}
                      </div>
                    </td>
                    <td className="py-2 px-4 text-slate-600">
                      {t.team_lead?.full_name || <span className="italic text-slate-400">Unassigned</span>}
                    </td>
                    <td className="py-2 px-4 text-slate-600">{t.member_count}</td>
                    <td className="py-2 px-4">
                      {t.status === 'active'
                        ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Active</Badge>
                        : <Badge className="bg-slate-200 text-slate-600 text-[10px]">Archived</Badge>}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" title="Manage members"
                                onClick={() => setDetail(t)}
                                data-testid={`team-manage-btn-${t.id}`}>
                          <Users className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" title="History"
                                onClick={() => openHistory(t)}
                                data-testid={`team-history-btn-${t.id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        {t.status === 'active' ? (
                          <Button variant="ghost" size="sm" title="Archive"
                                  onClick={() => archive(t)}
                                  data-testid={`team-archive-btn-${t.id}`}>
                            <Archive className="h-3.5 w-3.5 text-amber-600" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" title="Restore"
                                  onClick={() => restore(t)}
                                  data-testid={`team-restore-btn-${t.id}`}>
                            <RotateCcw className="h-3.5 w-3.5 text-emerald-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {createOpen && (
        <CreateTeamDialog open={createOpen} onOpenChange={setCO} types={types}
                          companies={companies} users={users}
                          onCreated={() => { setCO(false); reload(); }} />
      )}

      {detailTeam && (
        <TeamDetailDialog team={detailTeam} teams={teams} users={users}
                          onClose={() => { setDetail(null); reload(); }} />
      )}

      {historyRows && (
        <HistoryDialog data={historyRows} onClose={() => setHistoryRows(null)} />
      )}
    </div>
  );
}


function CreateTeamDialog({ open, onOpenChange, types, companies, users, onCreated }) {
  const [form, setForm] = useState({ name: '', team_type: 'sales',
                                     company_id: '', team_lead_user_id: '',
                                     notes: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.team_type) {
      toast.error('Team name and team type are required.'); return;
    }
    setBusy(true);
    try {
      await api.post('/teams', {
        name: form.name.trim(),
        team_type: form.team_type,
        company_id: form.company_id || null,
        team_lead_user_id: form.team_lead_user_id || null,
        notes: form.notes || null,
      });
      toast.success(`Team "${form.name}" created.`);
      onCreated();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Create failed.')); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="team-create-dialog">
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Team Name *</Label>
            <Input value={form.name}
                   onChange={e => setForm({ ...form, name: e.target.value })}
                   data-testid="team-create-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Team Type *</Label>
              <Select value={form.team_type}
                      onValueChange={v => setForm({ ...form, team_type: v })}>
                <SelectTrigger data-testid="team-create-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Company</Label>
              <Select value={form.company_id}
                      onValueChange={v => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="team-create-company"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Team Lead</Label>
            <Select value={form.team_lead_user_id}
                    onValueChange={v => setForm({ ...form, team_lead_user_id: v })}>
              <SelectTrigger data-testid="team-create-lead"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                {users.filter(u => u.is_active !== false).map(u =>
                  <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes}
                      onChange={e => setForm({ ...form, notes: e.target.value })}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="team-create-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Create Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TeamDetailDialog({ team, teams, users, onClose }) {
  const [detail, setDetail]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [renameOpen, setRO]   = useState(false);
  const [renameForm, setRF]   = useState({ name: team.name, team_lead_user_id: team.team_lead?.id || '', reason: '' });
  const [moveOpen, setMO]     = useState(false);
  const [moveForm, setMF]     = useState({ user_id: '', target_team_id: '', reason: '', confirmed: false });
  const [addUserId, setAUI]   = useState('');

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/teams/${team.id}`);
      setDetail(r.data?.team || null);
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Load failed.')); }
  }, [team.id]);
  useEffect(() => { reload(); }, [reload]);

  const addMember = async () => {
    if (!addUserId) return;
    setBusy(true);
    try {
      await api.post(`/teams/${team.id}/members`, { user_id: addUserId });
      toast.success('Member added.'); setAUI(''); reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Add failed.')); }
    finally { setBusy(false); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm('Remove this member from the team?')) return;
    setBusy(true);
    try {
      await api.delete(`/teams/${team.id}/members/${uid}`);
      toast.success('Member removed.'); reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Remove failed.')); }
    finally { setBusy(false); }
  };
  const submitRename = async () => {
    setBusy(true);
    try {
      await api.patch(`/teams/${team.id}`, {
        name: renameForm.name,
        team_lead_user_id: renameForm.team_lead_user_id || null,
        reason: renameForm.reason || null,
      });
      toast.success('Team updated.'); setRO(false); reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Update failed.')); }
    finally { setBusy(false); }
  };
  const submitMove = async () => {
    if (!moveForm.confirmed || !moveForm.reason.trim()) {
      toast.error('Confirmation and reason are required.'); return;
    }
    setBusy(true);
    try {
      await api.post(`/teams/${team.id}/move-member`, {
        user_id: moveForm.user_id,
        target_team_id: moveForm.target_team_id,
        reason: moveForm.reason,
        confirmed: true,
      });
      toast.success('Member moved.'); setMO(false); reload();
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Move failed.')); }
    finally { setBusy(false); }
  };

  const otherTeams = teams.filter(t => t.id !== team.id && t.status === 'active');
  const nonMembers = users.filter(u =>
    u.is_active !== false &&
    !(detail?.members || []).some(m => m.id === u.id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="team-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-600" />
            {detail?.name || team.name}
            <Badge className={`text-[10px] ${TYPE_TONES[detail?.team_type] || 'bg-slate-100'}`}>
              {detail?.team_type_label || team.team_type_label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {!detail ? (
          <div className="py-6 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin"/> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1"><Building2 className="h-3 w-3"/>{detail.company_name}</div>
              <div>Lead: <span className="text-slate-800 font-medium">
                {detail.team_lead?.full_name || 'Unassigned'}</span></div>
              <div>Members: <span className="text-slate-800 font-medium">{detail.member_count}</span></div>
              <Button variant="outline" size="sm" onClick={() => setRO(true)}
                      data-testid="team-rename-open">
                <Edit3 className="h-3 w-3 mr-1"/> Rename / Change Lead
              </Button>
            </div>

            <div className="rounded border">
              <div className="px-3 py-2 border-b flex items-center justify-between bg-slate-50">
                <div className="text-xs font-semibold text-slate-700">Members</div>
                <div className="flex items-center gap-2">
                  <select className="text-xs border rounded px-2 py-1"
                          value={addUserId}
                          onChange={e => setAUI(e.target.value)}
                          data-testid="team-add-member-select">
                    <option value="">+ Add member…</option>
                    {nonMembers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                  </select>
                  <Button size="sm" onClick={addMember} disabled={busy || !addUserId}
                          className="bg-emerald-600 hover:bg-emerald-700 h-7"
                          data-testid="team-add-member-btn">
                    <UserPlus className="h-3 w-3 mr-1"/> Add
                  </Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(detail.members || []).map(m => (
                    <tr key={m.id} className="border-b last:border-0"
                        data-testid={`team-member-row-${m.id}`}>
                      <td className="py-2 px-3 font-medium">{m.full_name}
                        {detail.team_lead?.id === m.id && (
                          <Badge className="ml-2 text-[9px] bg-indigo-100 text-indigo-800">LEAD</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">{m.email}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{m.role}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" title="Move to another team"
                                  disabled={detail.team_lead?.id === m.id}
                                  onClick={() => { setMF({ user_id: m.id, target_team_id: '', reason: '', confirmed: false }); setMO(true); }}
                                  data-testid={`team-member-move-${m.id}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5 text-orange-600"/>
                          </Button>
                          <Button variant="ghost" size="sm" title="Remove"
                                  disabled={detail.team_lead?.id === m.id}
                                  onClick={() => removeMember(m.id)}
                                  data-testid={`team-member-remove-${m.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500"/>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(detail.members || []).length === 0 && (
                    <tr><td colSpan={4} className="py-3 px-3 text-center italic text-xs text-slate-400">No members yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Rename / change lead */}
        {renameOpen && (
          <Dialog open onOpenChange={setRO}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Rename Team / Change Lead</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Team Name</Label>
                  <Input value={renameForm.name}
                         onChange={e => setRF({ ...renameForm, name: e.target.value })}
                         data-testid="team-rename-name"/></div>
                <div><Label className="text-xs">Team Lead</Label>
                  <Select value={renameForm.team_lead_user_id}
                          onValueChange={v => setRF({ ...renameForm, team_lead_user_id: v })}>
                    <SelectTrigger data-testid="team-rename-lead"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.is_active !== false).map(u =>
                        <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Reason (audited)</Label>
                  <Textarea rows={2} value={renameForm.reason}
                            onChange={e => setRF({ ...renameForm, reason: e.target.value })}
                            data-testid="team-rename-reason"/></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRO(false)}>Cancel</Button>
                <Button onClick={submitRename} disabled={busy}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="team-rename-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Move member */}
        {moveOpen && (
          <Dialog open onOpenChange={setMO}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Move Member to Another Team</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-500">
                  Moving: <span className="text-slate-800 font-semibold">
                    {(detail?.members || []).find(m => m.id === moveForm.user_id)?.full_name}
                  </span>
                </div>
                <div><Label className="text-xs">Destination Team</Label>
                  <Select value={moveForm.target_team_id}
                          onValueChange={v => setMF({ ...moveForm, target_team_id: v })}>
                    <SelectTrigger data-testid="team-move-target"><SelectValue placeholder="Select team"/></SelectTrigger>
                    <SelectContent>
                      {otherTeams.map(t => <SelectItem key={t.id} value={t.id}>
                        {t.name} ({t.team_type_label})
                      </SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Reason (required, audited)</Label>
                  <Textarea rows={2} value={moveForm.reason}
                            onChange={e => setMF({ ...moveForm, reason: e.target.value })}
                            data-testid="team-move-reason"/></div>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={moveForm.confirmed}
                         onChange={e => setMF({ ...moveForm, confirmed: e.target.checked })}
                         data-testid="team-move-confirm"/>
                  I understand this reassigns the employee across teams.
                </label>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setMO(false)}>Cancel</Button>
                <Button onClick={submitMove} disabled={busy || !moveForm.target_team_id}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="team-move-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Move Member'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}


function HistoryDialog({ data, onClose }) {
  const { team, items } = data;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="team-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4"/> {team.name} — History
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {items.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No audit rows.</div>}
          {items.map(r => (
            <div key={r.id} className="border rounded p-2 text-xs" data-testid={`team-history-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-indigo-700">{r.action}</span>
                <span className="text-slate-400">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</span>
              </div>
              <div className="text-slate-600 mt-1">By: <span className="text-slate-800">
                {(r.actor || {}).full_name || (r.actor || {}).email || '—'}
              </span></div>
              {r.reason && <div className="text-slate-500 italic mt-1">&ldquo;{r.reason}&rdquo;</div>}
              {r.payload && Object.keys(r.payload).length > 0 && (
                <pre className="mt-1 bg-slate-50 rounded p-1.5 text-[10px] text-slate-600 overflow-x-auto">{JSON.stringify(r.payload, null, 2)}</pre>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
